import { all, take, put, call, race, select, takeLatest } from 'redux-saga/effects'
import { routerActions } from 'connected-react-router'
import _ from 'lodash/fp'
import localStore from 'localforage'

import { postEffect } from './network'
import { types, actions, selectors as authSelectors } from '../reducers/auth'

import * as profileReducer from '../reducers/profile'

export const TOKEN_KEY = 'glfr_token'
export const LOGIN_RETURN_TO_KEY = 'mlg_login_return_to'

/*
 * Will create an object like
 *   {
 *    path: '/somewhere'
 *    os: 'windoze'
 *   },
 * from the string "?path=/somewhere&os=windoze".
 * And then search for the key or return the fallback
 */
const getSearchParam = ({ searchString, key, fallback = '/' }) => {
  return new URLSearchParams(searchString).get(key) || fallback
}

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * loginEffect ({ email, password, provider }) {
  const request = {
    authenticated: false,
    url: 'authenticate',
    options: {
      body: {
        username: email,
        password,
        provider
      }
    }
  }
  const chan = yield call(postEffect, request)
  const { error, ...loginResponse } = yield take(chan)
  return error
    ? { error }
    : { ...loginResponse }
}

export function * renewSessionEffect () {
  const request = {
    url: 'token',
    options: {
      body: {
        version: navigator && navigator.appVersion
      }
    }
  }
  const chan = yield call(postEffect, request)
  const { error, token, expired_at: expiredAt } = yield take(chan)
  return error
    ? { error }
    : { token, expiredAt }
}

export function clearToken () {
  return localStore.clear()
}

export function * saveToken (tokenObject) {
  return yield localStore.setItem(TOKEN_KEY, tokenObject)
}

export function * loadToken () {
  const token = yield select(authSelectors.token)
  return token ? { token } : (yield localStore.getItem(TOKEN_KEY))
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

/**
 * takeLatest(types.LOGIN_REQUEST, loginFlow)
 */
export function * loginFlow ({ payload }) {
  const first = yield race({
    login: call(loginEffect, payload),
    logout: take(types.LOGOUT)
  })
  // the wait is over
  yield put(actions.loginRequestDone())

  const avoidRedirects = Boolean(payload.avoidRedirects)

  if (first.login) {
    const { error, ...loginResponse } = first.login
    if (error) {
      yield put(actions.loginError({ error, avoidRedirects }))
    } else {
      const searchString = yield select(_.get('router.location.search'))
      const navigateTo = getSearchParam({
        searchString,
        key: 'returnTo',
        fallback: window.sessionStorage.getItem(LOGIN_RETURN_TO_KEY) || '/welcome'
      })

      yield all([
        put(actions.loginSuccess({ ...loginResponse, avoidRedirects })),
        call(saveToken, loginResponse),
        put(profileReducer.actions.loadRequest(Date.now()))
      ])

      const profileResult = yield take([profileReducer.types.LOAD_SUCCESS, profileReducer.types.LOAD_ERROR])

      if (profileResult.type === profileReducer.types.LOAD_ERROR) {
        // Login went well, but profile could not be loaded
        yield put(routerActions.replace('/'))
      } else {
        // Optionally redirect with [handle]/ replaced by actual handle if it's in the path
        const handle = _.getOr('', 'payload.handle', profileResult)
        window.sessionStorage.removeItem(LOGIN_RETURN_TO_KEY)
        yield put(routerActions.replace(_.replace('[handle]/', `${handle}/`, navigateTo)))
      }
    }
  } else if (first.logout) {
    yield call(logoutFlow)
  }
}

/**
 * takeLatest(types.LOGOUT, logoutFlow)
 */
export function * logoutFlow () {
  yield call(clearToken)
  window.location.pathname = '/login'
}

/**
 * takeLatest(types.RENEW_SESSION_REQUEST, renewSessionFlow)
 */
export function * renewSessionFlow () {
  const { token, expired_at: expiredAt, error } = yield call(renewSessionEffect)
  yield put(profileReducer.actions.loadRequest(Date.now()))
  if (error) {
    yield call(clearToken)
  } else {
    yield put(actions.renewSessionSuccess({ token, expiredAt }))
  }
}

/**
 * call(takeLatest, types.BOOTSTRAP_REQUEST, bootstrapFlow)
 */
export function * bootstrapFlow () {
  const tokenObject = yield call(loadToken)
  yield put(actions.loadingBegin())
  if (tokenObject) {
    yield put(actions.setToken(tokenObject))
    yield call(renewSessionFlow)
  } else {
    yield put(actions.loadingDone())
  }
  yield put(actions.bootstrapDone())
}

export default function * () {
  return yield all([
    takeLatest(types.LOGIN_REQUEST, loginFlow),
    takeLatest(types.LOGOUT, logoutFlow),
    takeLatest(types.RENEW_SESSION_REQUEST, renewSessionFlow),
    takeLatest(types.BOOTSTRAP_REQUEST, bootstrapFlow)
  ])
}
